// ChatMap Hills SP3b — the keystone match-or-create input. Mounted by HillChip
// (trail), SpineHillEditPopover (in-node spine edit), and PartyTray (set-out).
// /goals has no search param → fetch a page of each type + client-filter.
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { workService } from '../../services/workService';
import { useTheme } from '../../contexts/ThemeContext';
import type { TargetRef, ResolvedHillTarget } from '../../types/hill';

interface Match { entity_type: 'goal' | 'project' | 'task'; entity_id: string; title: string; }

export function HillTargetCombobox({ current, onPick, placeholder, autoFocus = true }: {
  current: ResolvedHillTarget | null;
  onPick: (target: TargetRef | null) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const { darkMode } = useTheme();
  const [text, setText] = useState('');
  const [creating, setCreating] = useState(false);
  const goalsQ = useQuery(['hill-pick-goals'], () => workService.getGoals({ limit: 200 }));
  const projectsQ = useQuery(['hill-pick-projects'], () => workService.getProjects({ limit: 200 }));
  const tasksQ = useQuery(['hill-pick-tasks'], () => workService.getTasks({ limit: 200 }));
  const anyLoading = goalsQ.isLoading || projectsQ.isLoading || tasksQ.isLoading;
  const q = text.trim().toLowerCase();
  const all: Match[] = [
    ...((goalsQ.data ?? []) as any[]).map((g) => ({ entity_type: 'goal' as const, entity_id: g.id, title: g.title })),
    ...((projectsQ.data ?? []) as any[]).map((p) => ({ entity_type: 'project' as const, entity_id: p.id, title: p.title })),
    ...((tasksQ.data ?? []) as any[]).map((t) => ({ entity_type: 'task' as const, entity_id: t.id, title: t.title })),
  ];
  const matches = q ? all.filter((m) => m.title.toLowerCase().includes(q)).slice(0, 8) : [];
  const exact = !!q && all.some((m) => m.title.toLowerCase() === q);
  // Suppress create while loading → no duplicate-create of a goal about to appear.
  const showCreate = !!q && !exact && !anyLoading;

  const createGoal = async () => {
    if (!text.trim() || creating) return;
    setCreating(true);
    try {
      const g = await workService.createGoal({ title: text.trim() });
      onPick({ entity_type: 'goal', entity_id: g.id });
      setText('');
    } finally { setCreating(false); }
  };

  return (
    <div data-testid="hill-combobox" className={`inline-flex flex-col gap-1 rounded border p-1 text-xs min-w-[220px] ${darkMode ? 'bg-gray-800 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}>
      {current && (
        <span data-testid="hill-combobox-current" className={`px-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          now: <span className="font-medium">{current.title}</span>
        </span>
      )}
      <input
        autoFocus={autoFocus}
        data-testid="hill-combobox-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder ?? 'type a goal — or create one'}
        className={`px-1.5 py-0.5 rounded outline-none ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}
      />
      {q && anyLoading && <span className={`px-1.5 py-0.5 italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>searching…</span>}
      {matches.map((m) => (
        <button key={`${m.entity_type}:${m.entity_id}`} type="button"
          data-testid={`hill-combobox-match-${m.entity_id}`}
          onClick={() => onPick({ entity_type: m.entity_type, entity_id: m.entity_id })}
          className={`text-left px-1.5 py-0.5 rounded ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
          <span className="opacity-60 mr-1" aria-hidden>{m.entity_type === 'goal' ? '🎯' : m.entity_type === 'project' ? '📁' : '✓'}</span>{m.title}
        </button>
      ))}
      {showCreate && (
        <button type="button" data-testid="hill-combobox-create" onClick={createGoal} disabled={creating}
          title="Creates a new Goal. Projects and tasks are made in the work board."
          className={`text-left px-1.5 py-0.5 rounded italic ${darkMode ? 'text-emerald-300 hover:bg-gray-700' : 'text-emerald-600 hover:bg-gray-100'}`}>
          {creating ? 'creating…' : `+ New goal "${text.trim()}"`}
        </button>
      )}
      {exact && !anyLoading && (
        <span data-testid="hill-combobox-exists" className={`px-1.5 py-0.5 italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          "{text.trim()}" already exists — pick it above
        </span>
      )}
      {current && (
        <button type="button" data-testid="hill-combobox-clear" onClick={() => onPick(null)}
          className={`text-left px-1.5 py-0.5 rounded ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}>
          ✕ clear target
        </button>
      )}
    </div>
  );
}
